import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import { environment } from '../../../../environments/environment';
import {Workspace} from '../models/work-space.model';
import {NbDialogRef} from '@nebular/theme';
import {map} from 'rxjs/operators';
import {Router} from '@angular/router';
import {fromJS} from 'immutable';
import {PermissionsService} from '../components/permissions/services/permissions.service';
import {ToastrWrapperService} from './toastr-wrapper.service';
import { AssetApi } from '../../main/modules/asset/api/asset.api';
import {SortOrder} from '../models/sort.order';
import { AssetFacade } from '../../main/modules/asset/facade/asset.facade';

@Injectable({
  providedIn: 'root',
})
export class WorkSpaceService {

  activateWorkSpaceID: BehaviorSubject<string> = new  BehaviorSubject<string>(null);
  newWorkspacesLoaded$: BehaviorSubject<any> = new  BehaviorSubject<any>(null);
  api = environment.api;
  private userWorkspaces$ = new BehaviorSubject<Partial<Workspace>[]>(null);
  private selectedWorkspace$ = new BehaviorSubject<Partial<Workspace>>(null);

  constructor(private http: HttpClient,
    private router: Router,
    private toastr: ToastrWrapperService,
    private permissionsService: PermissionsService,
    private assetApi: AssetApi,
    private assetFacade: AssetFacade,) {
  }

  get activeworkspaceId(): string {
    return this.activateWorkSpaceID.value;
  }

  setActiveWorkSpaceID(workspaceId: string) {
    this.activateWorkSpaceID.next(workspaceId);
  }

  getActiveWorkSpaceID(): Observable<string> {
    return this.activateWorkSpaceID.asObservable();
  }


  getUserWorkspaces$(): Observable<Partial<Workspace>[]> {
    return this.userWorkspaces$.asObservable();
  }

  getUserWorkspacesValue(): Partial<Workspace>[] {
    return this.userWorkspaces$.getValue();
  }

  loadUserWorkSpaces(sortOrder: SortOrder = 'None'): void {
    this.permissionsService.loadUserWorkspaces$(sortOrder).subscribe(workspaces => {
      this.updateUserWorkspaces(workspaces);
    });
  }

  createHomeWorkSpace(workspace: any, dialogRef: NbDialogRef<any>) {
    this.createWorkSpace(workspace).pipe(map(res => res.data)).subscribe(
      (res) => {
        this.router
          .navigateByUrl('RightSidebarComponent', { skipLocationChange: true })
          .then(() => {
            this.updateUserWorkspaces(null);
            this.router.navigate(['/home', res.workspaceId]);
          });
        this.setActiveWorkSpaceID(res.workspaceId);
        dialogRef.close();
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        dialogRef.close();
      },
    );
  }

  updateUserWorkspaces(workspaces: Partial<Workspace>[]): void {
    if(workspaces) {
      workspaces.forEach((workspace) => {
        workspace.assetType = 'Workspace';
        workspace.icon = 'home';
        workspace.link = `/home/${workspace.workspaceId}`;
        workspace.cardOptions = this.getWorkspaceCardOptions(workspace);
        workspace.noPermissionExist = !workspace.permissions.get('Edit').isChecked;
        workspace.imagePath = workspace.imagePath? `${environment.api}/File/Download?path=${workspace.imagePath}`
         :'assets/images/workspace.png';
      });
    }
    this.userWorkspaces$.next(workspaces);
  }

  updateUserWorkspace(newWorkspace: Partial<Workspace>): void {
    const userWorkspaces = [...this.userWorkspaces$.getValue()];
    const foundIndex = userWorkspaces.findIndex(workspace => workspace.workspaceId === newWorkspace.workspaceId);
    const selectedWorkspace = this.selectedWorkspace$.getValue();
    if(foundIndex > -1) {
      userWorkspaces[foundIndex].name = newWorkspace.name;
      this.userWorkspaces$.next(userWorkspaces);
    }
    if(selectedWorkspace.workspaceId === newWorkspace.workspaceId) {
      selectedWorkspace.name = newWorkspace.name;
      this.selectedWorkspace$.next(selectedWorkspace);
    }
  }

  updateUserWorkspaceData(newWorkspace: Partial<Workspace>): void {
    const userWorkspaces = [...this.userWorkspaces$.getValue()];
    const foundIndex = userWorkspaces.findIndex(workspace => workspace.workspaceId === newWorkspace.workspaceId);
    if(foundIndex > -1) {
      userWorkspaces[foundIndex] = {...newWorkspace};
      this.userWorkspaces$.next(userWorkspaces);
    }
  }

  updateSelectedWorkspace(selectedWorkspace: Partial<Workspace>): void {
    this.selectedWorkspace$.next(selectedWorkspace);
    this.setActiveWorkSpaceID(selectedWorkspace?.workspaceId);
  }

  updateSelectedWorkSpaceById(workspaceId: string): void {
    const userWorkspaces = this.userWorkspaces$.getValue();
    if(userWorkspaces !== null) {
      const selectedWorkspace = userWorkspaces.find(workspace => workspace.workspaceId === workspaceId);
      this.updateSelectedWorkspace(selectedWorkspace);
    }
  }

  addWorkSpace(workspace: Partial<Workspace>): void {
    workspace.icon = 'home';
    workspace.link = `/home/${workspace.workspaceId}`;
    const index = this.userWorkspaces$.getValue().length - 1;
    this.userWorkspaces$.getValue().splice(index,0, workspace);
  }

  filterWorkspaces(workspaceId: string): void {
    const filterWorkspaces =this.userWorkspaces$.getValue().filter(workspace => workspace.workspaceId !== workspaceId);
    this.userWorkspaces$.next(filterWorkspaces);
  }

  getSelectedWorkspaceValue(): Partial<Workspace> {
    return this.selectedWorkspace$.getValue();
  }

  getSelectedWorkspace$(): Observable<Partial<Workspace>> {
    return this.selectedWorkspace$.asObservable();
  }

  resetStateUserWorkspaces(): void {
    this.updateUserWorkspaces(null);
  }

  createWorkSpace(workSpace: any): Observable<any> {
    return this.http.post(`${this.api}/Workspace/CreateWorkspace`, workSpace);
  }

  getRole(roleId: string): Observable<any> {
    return this.http.get<any>(`${this.api}/Role/GetRole/${roleId}`).pipe(map(res => res.data));
  }

  updateImageWorkspace(body: any): Observable<any> {
    return this.http.post(`${this.api}/Workspace/UpdateWorkspaceImage`, body);
  }

  updateWorkspacePinStatus(workspaceId: string): Observable<any> {
    return this.http.post(`${this.api}/Workspace/UpdateWorkspacePinStatus/${workspaceId}`, {});
  }

  getWorkspaceCardOptions(workspace: Partial<Workspace>): any {
    return [
      { title: 'تغییر نام', value: 'workspaceEditName', isPermitted: true },
      { title: 'افزودن عکس', value: 'workspaceUploadImage', isPermitted: workspace.imagePath === null },
      { title: 'تغییر عکس', value: 'workspaceUploadImage', isPermitted: workspace.imagePath !== null },
      { title: 'حذف عکس', value: 'workspaceRemoveImage', isPermitted: workspace.imagePath !== null },
      { title: 'سنجاق کردن', value: 'workspacePinStatus', isPermitted: !workspace.isPined },
      { title: 'برداشتن سنجاق', value: 'workspacePinStatus', isPermitted: workspace.isPined },
    ];
  }

  uploadWorkspaceImage(formFile: any, workspace: Partial<Workspace>, sortOrder: SortOrder = 'None', loadingAssets?:any) {
    this.assetApi.uploadFile('WorkspaceAvatars', formFile, workspace.workspaceId).subscribe(
      (res: any) => {

        const imagePath = res.data.path;
        const body = {
          workspaceId: workspace.workspaceId,
          imagePath,
        };
        this.updateImageWorkspace(body).subscribe((_res: any) => {
          if(loadingAssets){
            this.assetFacade.loadHomePaginatedWorkspaces(loadingAssets);
          }else{
            workspace.imagePath = imagePath ? `${environment.api}/File/Download?path=${imagePath}` :
              'assets/images/workspace.png';
            this.updateUserWorkspaceData(workspace);
          }
          this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          });
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  removeWorkspaceImage(workspace: Partial<Workspace>, sortOrder: SortOrder = 'None', loadingAssets?:any) {
    const body = {
      workspaceId: workspace.workspaceId,
      imagePath: null,
    };
    this.updateImageWorkspace(body).subscribe((_res: any) => {
      this.loadUserWorkSpaces(sortOrder);
      if(loadingAssets){
        this.assetFacade.loadHomePaginatedWorkspaces(loadingAssets);
      }
      this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
    },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      });
  }

  changeWorkspacePinStatus(workspaceIdForUnpin: string, sortOrder: SortOrder, pageNumber?:number, workSpaceId?: string): void {
    this.updateWorkspacePinStatus(workspaceIdForUnpin).subscribe(() => {
      this.loadUserWorkSpaces(sortOrder);

      const body = {
        assetType: 'None',
        takeNumber: 100,
        workspaceId: workSpaceId,
        pageNumber: 1,
        pageSize: pageNumber*7,
        needCount: true,
        dashboardGroupCount: 100,
      };
      if(pageNumber){
        this.assetFacade.loadHomePaginatedWorkspaces(body);
       }
    });
  }

}
